import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Checkbox, Col, DatePicker, Input, Modal, Radio, RadioChangeEvent, Row, Select, Space, Table, message } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { UploadDocument } from "@qualitas/upload-document";
import ColumnGroup from "antd/lib/table/ColumnGroup";
import Column from "antd/lib/table/Column";
import moment from "moment";

import FormLayout from "../../FormLayout/FormLayout";
import SuitabilityTestQCF from "./SuitabilityTestQCF";
import SuitabilityTestPT from "./SuitabilityTestPT";

import { InvestorAPI } from "../../../Api/InvestorAPI";
import { SuitabilityTestAPI } from "../../../Api/SuitabilityTestAPI";
import { ContractAPI } from "../../../Api/ContractAPI";
import { DocumentsAPI } from "../../../Api/DocumentsAPI";
import { useSuitabiltyTest } from "../../../Hooks/useSuitabilityTest/useSuitabiltyTest";

import { ISuitabilityTest } from "./ISuitabilityTest";
import { Document, Record8 } from "../../../Models";

import "./SuitabilityTest.scss";

const { Option } = Select;
const MIFID_COMMITMENT_THRESHOLD = 100000;

type MifidMode = "convenience" | "appropriateness" | null;

const StandardSuitabilityTest = ({
	baseURL,
	baseURLExternal,
	translations,
	clientId,
	moduleRole,
	unlockNextStep,
	isReadOnly = false
}: ISuitabilityTest) => {
	const navigate = useNavigate();

	const suitabilityTestAPI = useMemo(() => SuitabilityTestAPI(baseURL, clientId), [baseURL, clientId]);
	const investorAPI = useMemo(() => InvestorAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const contractAPI = useMemo(() => ContractAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const documentsAPI = useMemo(() => DocumentsAPI(baseURL, clientId), [baseURL, clientId]);

	const [mifidMode, setMifidMode] = useState<MifidMode>(null);
	const [suitabilityDocument, setSuitabilityDocument] = useState<Document | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	const {
		contract,
		isSuitabilityDone,
		errors,
		investorInfo,
		testResponses,
		isOpenConfirmModal,
		contractId,
		onQuestionAnswered,
		saveTest,
		isContinuationFinance,
		getQ4Title,
		onCancelContinue,
		toNextStep,
	} = useSuitabiltyTest({
		suitabilityTestAPI,
		unlockNextStep,
		navigate,
		investorAPI,
		contractAPI,
		translations
	});

	const isLowCommitment = useMemo(
		() => Number(contract?.commitment ?? 0) < MIFID_COMMITMENT_THRESHOLD,
		[contract?.commitment]
	);

	const isSuitabilityDocumentSaved = Boolean(suitabilityDocument?.id);
	const isAppropriatenessMode = mifidMode === "appropriateness";
	const formTitle = isAppropriatenessMode ? translations.mifidSuitabilityTest : translations.mifidConvenienceTest;
	const formSubTitle = isAppropriatenessMode ? translations.mifidSuitabilityText : translations.suitability_text1;

	useEffect(() => {
		if (!contractId) {
			return;
		}

		setLoading(true);
		documentsAPI.getSuitabilityTestDocument(contractId)
			.then((res: any) => {
				if (res.success) {
					setSuitabilityDocument(res.data?.suitabilityTestDocument ?? null);
				}
			})
			.finally(() => setLoading(false));
	}, [contractId]);

	useEffect(() => {
		if (!contractId || contract === undefined) {
			return;
		}

		if (!isLowCommitment) {
			setMifidMode("convenience");
			return;
		}

		if (isSuitabilityDone) {
			setMifidMode("convenience");
			return;
		}

		if (isSuitabilityDocumentSaved) {
			setMifidMode("appropriateness");
			return;
		}

		setMifidMode("appropriateness");
	}, [contract, contractId, isLowCommitment, isSuitabilityDone, isSuitabilityDocumentSaved]);

	const handleSuitabilityDocumentUpload = (fileUrl: string) => {
		setSuitabilityDocument((prevState) => ({
			...(prevState ?? { idRegistry: 0 }),
			type: "Suitability Test",
			url: fileUrl,
			status: "Draft",
			description: undefined,
			idRegistry: 0,
		}));
	};

	const saveSuitabilityDocument = async () => {
		if (!contractId || !suitabilityDocument?.url) {
			message.error(translations.completeCampo);
			return;
		}

		setLoading(true);
		const payload = {
			contractId,
			suitabilityTestDocument: {
				...suitabilityDocument,
				type: "Suitability Test",
				status: "Draft",
				description: undefined,
				idRegistry: 0,
			}
		};

		try {
			const response = suitabilityDocument.id
				? await documentsAPI.updateSuitabilityTestDocument(payload)
				: await documentsAPI.saveSuitabilityTestDocument(payload);

			if (response.success) {
				setSuitabilityDocument(response.data?.suitabilityTestDocument ?? null);
				setMifidMode("appropriateness");
				message.success(translations.datosGuardados);
				unlockNextStep(contractId);
			} else {
				message.error(translations.messageError);
			}
		} catch (error) {
			message.error(translations.messageError);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = () => {
		if (mifidMode === "appropriateness") {
			saveSuitabilityDocument();
			return;
		}

		saveTest();
	};

	return (
		<Row className="suitability-test">
			<FormLayout
				translations={translations}
				loading={loading}
				title={formTitle}
				subTitle={formSubTitle}
				onClick={handleSave}
				disabledSaveButton={isSuitabilityDone}
				contractId={contractId}
				nextStep={unlockNextStep}
				enableNextStep={true}
				forceEnableNextStep={isReadOnly}
				hideSaveButton={isReadOnly}
			>
				{isSuitabilityDone && (
					<Col span={10}>
						<Alert message="Test completado correctamente" type="success" showIcon />
					</Col>
				)}
				{!isSuitabilityDone && isSuitabilityDocumentSaved && (
					<Col span={10}>
						<Alert message={translations.suitabilityTestSent} type="success" showIcon />
					</Col>
				)}
				{mifidMode === "appropriateness" && (
					<Col span={24}>
						<Row gutter={[16, 16]} className="test">
							<Col span={24}>
								<UploadDocument
									baseURL={baseURL}
									clientId={clientId}
									moduleRole={moduleRole}
									translations={{
										label: translations.suitabilityUploadLabel,
										uploadDocument: translations.uploadDocument,
										uploadDocumentTooltip: translations.allowedFileFormats,
										selectFiles: translations.selectFiles,
										changeFile: translations.changeFile,
									}}
									mandatory={true}
									oneRow={false}
									uploadedFile={suitabilityDocument?.url || ""}
									disabled={isReadOnly}
									error={false}
									shouldRunOcr={false}
									ocrFields={[]}
									updateFileUrl={handleSuitabilityDocumentUpload}
									fileName="Suitability-Test"
									maxDocuments={3}
									allowMultipleDocuments
								/>
							</Col>
						</Row>
					</Col>
				)}
				{mifidMode === "convenience" && (
					!isContinuationFinance()
						? (
							<Col span={24}>
								<Row gutter={[16, 16]} className="info">
									<strong>{translations.suitability_title1}</strong>
									<ol>
										<li>{translations.suitability_p1}</li>
										<li>{translations.suitability_p2}</li>
										<li>{translations.suitability_p3}</li>
										<li>{translations.suitability_p4}</li>
									</ol>
								</Row>
								<Row gutter={[16, 16]} className="test">
									<Col span="6" className="titles">
										<label className="required"> {translations.nombre}</label>
										<Input
											type="text"
											className="input-field"
											name="name"
											value={investorInfo.name!}
											disabled
											status={errors.name ? "error" : ""}
										/>
									</Col>
									<Col span="6" className="titles">
										<label className="required"> {translations.firstLastnameSignatory}</label>
										<Input
											type="text"
											className="input-field"
											name="surname"
											value={investorInfo.surname!}
											disabled
											status={errors.surname ? "error" : ""}
										/>
									</Col>
									<Col span="6" className="titles">
										<label>{translations.secondLastnameSignatory || "Segundo apellido"}</label>
										<Input
											type="text"
											className="input-field"
											name="secondLastName"
											value={investorInfo.secondLastName ?? ""}
											disabled
										/>
									</Col>
									<Col span="6" className="titles">
										<label className="required">{translations.dateOfCompletion || "Fecha de cumplimentación"}</label>
										<DatePicker
											className="input-field"
											name="dateOfCompletion"
											value={testResponses.dateOfCompletion ? moment(testResponses.dateOfCompletion) : undefined}
											format="DD/MM/YYYY"
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(date) => onQuestionAnswered("dateOfCompletion")(date ? date.format("YYYY-MM-DD") : null)}
											status={errors.dateOfCompletion ? "error" : ""}
										/>
										<span className={`error ${errors.dateOfCompletion ? "show" : null}`}>{translations.completeCampo}</span>
									</Col>
									<Col span="6" className="titles">
										<label className="required"> {translations.birthDate}</label>
										<DatePicker
											className="input-field"
											name="birthDate"
											value={investorInfo.birthDate ? moment(investorInfo.birthDate) : undefined}
											format="DD/MM/YYYY"
											disabled
											status={errors.birthDate ? "error" : ""}
										/>
									</Col>
									<Col span="6" className="titles">
										<label className="required"> {translations.documentNumber}</label>
										<Input
											type="text"
											className="input-field"
											name="dni"
											value={investorInfo.dni}
											disabled
											status={errors.dni ? "error" : ""}
										/>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q1}</strong>
										<span className={`error ${errors.Q1 ? "show" : null}`}>{translations.completeCampo}</span>
										<Checkbox
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(e: any) => onQuestionAnswered("q1_1")(e.target.checked)}
											checked={testResponses.q1_1 ?? false}>{translations.suitability_q1_01}
										</Checkbox>
										<br />
										<Checkbox
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(e: any) => onQuestionAnswered("q1_2")(e.target.checked)}
											checked={testResponses.q1_2 ?? false}>{translations.suitability_q1_02}
										</Checkbox>
										<br />
										<Checkbox
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(e: any) => onQuestionAnswered("q1_3")(e.target.checked)}
											checked={testResponses.q1_3 ?? false}>{translations.suitability_q1_03}
										</Checkbox>
										<Checkbox
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(e: any) => onQuestionAnswered("q1_4")(e.target.checked)}
											checked={testResponses.q1_4 ?? false}>{translations.suitability_q1_04}
										</Checkbox>
										<Checkbox
											disabled={isSuitabilityDone || isReadOnly}
											onChange={(e: any) => onQuestionAnswered("q1_5")(e.target.checked)}
											checked={testResponses.q1_5 ?? false}>{translations.suitability_q1_05}
										</Checkbox>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q2}</strong>
										<span className={`error ${errors.Q2 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q2")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q2}>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q2_01}</Radio>
												<Radio value={2}>{translations.suitability_q2_02}</Radio>
												<Radio value={3}>{translations.suitability_q2_03}</Radio>
												<Radio value={4}>{translations.suitability_q2_04}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q3}</strong>
										<span className={`error ${errors.Q3 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q3")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q3}
										>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q3_01}</Radio>
												<Radio value={2}>{translations.suitability_q3_02}</Radio>
												<Radio value={3}>{translations.suitability_q3_03}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{getQ4Title()}</strong>
										<span className={`error ${errors.Q4 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q4")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q4}
										>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q4_01}</Radio>
												<Radio value={2}>{translations.suitability_q4_02}</Radio>
												<Radio value={3}>{translations.suitability_q4_03}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q5}</strong>
										<span className={`error ${errors.Q5 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q5")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q5}
										>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q5_01}</Radio>
												<Radio value={2}>{translations.suitability_q5_02}</Radio>
												<Radio value={3}>{translations.suitability_q5_03}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q6}</strong>
										<span className={`error ${errors.Q6 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q6")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q6}
										>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q6_01}</Radio>
												<Radio value={2}>{translations.suitability_q6_02}</Radio>
												<Radio value={3}>{translations.suitability_q6_03}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q7}</strong>
										<span className={`error ${errors.Q7 ? "show" : null}`}>{translations.completeCampo}</span>
										<Radio.Group
											onChange={(e: RadioChangeEvent) => onQuestionAnswered("q7")(e.target.value)}
											disabled={isSuitabilityDone || isReadOnly}
											value={testResponses.q7}
										>
											<Space direction="vertical">
												<Radio value={1}>{translations.suitability_q7_01}</Radio>
												<Radio value={2}>{translations.suitability_q7_02}</Radio>
												<Radio value={3}>{translations.suitability_q7_03}</Radio>
											</Space>
										</Radio.Group>
									</Col>
									<Col span="24" className="titles">
										<strong>{translations.suitability_q8}</strong>
										<Table
											className="table"
											pagination={false}
											dataSource={[
												{ key: "0", tool: translations.suitability_q8_01, ops: "q8_1_Ops", vol: "q8_1_Vol", questionNumber: "1" },
												{ key: "1", tool: translations.suitability_q8_02, ops: "q8_2_Ops", vol: "q8_2_Vol", questionNumber: "2" },
												{ key: "2", tool: translations.suitability_q8_03, ops: "q8_3_Ops", vol: "q8_3_Vol", questionNumber: "3" },
												{ key: "3", tool: translations.suitability_q8_04, ops: "q8_4_Ops", vol: "q8_4_Vol", questionNumber: "4" },
												{ key: "4", tool: translations.suitability_q8_05, ops: "q8_5_Ops", vol: "q8_5_Vol", questionNumber: "5" },
												{ key: "5", tool: translations.suitability_q8_06, ops: "q8_6_Ops", vol: "q8_6_Vol", questionNumber: "6" }
											]}
										>
											<Column title="Instrumento" dataIndex="tool" key="tool" />
											<ColumnGroup title={translations.volumeOp}>
												<Column title={translations.qOps} dataIndex="1" key="01" render={(_: any, record: Record8) => (
													<Select
														style={{ width: "100%" }}
														onChange={(e) => onQuestionAnswered(record.ops)(e)}
														placeholder="Select one option"
														value={testResponses[record.ops]}
														disabled={isSuitabilityDone || isReadOnly}
														status={errors[record.ops] ? "error" : ""}
													>
														<Option key="01" value={1}>{translations.suitability_q8_lessThan2}</Option>
														<Option key="02" value={2}>{translations.suitability_q8_moreThan2}</Option>
														<Option key="03" value={3}>{translations.suitability_q8_none}</Option>
													</Select>
												)} />
												<Column title={translations.vOps} dataIndex="2" key="02" render={(_: any, record: Record8) => (
													<Select
														style={{ width: "100%" }}
														onChange={(e) => onQuestionAnswered(record.vol)(e)}
														value={testResponses[record.vol]}
														disabled={isSuitabilityDone || isReadOnly || testResponses[record.ops] === 3}
														status={errors[record.vol] ? "error" : ""}
													>
														<Option key="04" value={1}>{translations.suitability_q8_lessThan25k}</Option>
														<Option key="05" value={2}>{translations.suitability_q8_lessThan150k}</Option>
														<Option key="06" value={3}>{translations.suitability_q8_lessThan500k}</Option>
														<Option key="07" value={4}>{translations.suitability_q8_moreThan500k}</Option>
													</Select>
												)} />
											</ColumnGroup>
										</Table>
									</Col>
								</Row>
							</Col>
						)
						: <SuitabilityTestQCF
							errors={errors}
							investorInfo={investorInfo}
							isSuitabilityDone={isSuitabilityDone}
							onQuestionAnswered={onQuestionAnswered}
							testResponses={testResponses}
							getQ4Title={getQ4Title}
							translations={translations}
							isReadOnly={isReadOnly}
						/>
				)}
			</FormLayout>
			<Modal
				className="not-suitable-confirmation-modal"
				width={600}
				open={isOpenConfirmModal}
				closeIcon={null}
				centered
				footer={[
					<Row justify="space-between">
						<Col>
							<Button onClick={onCancelContinue}>{translations.cancel}</Button>
						</Col>
						<Col>
							<Button type="primary" onClick={toNextStep} disabled={isReadOnly}>{translations.continue}</Button>
						</Col>
					</Row>
				]}
			>
				<Row className="not-suitable-text" justify="center" align="middle">
					<h1><WarningFilled /></h1>
					<h2>{translations.suitabilityResult}</h2>
				</Row>
			</Modal>
		</Row>
	);
};

const SuitabilityTest = (props: ISuitabilityTest) => {
	const { suitabilityTestType = 1 } = props;

	if (suitabilityTestType === 2) {
		return <SuitabilityTestPT {...props} />;
	}

	return <StandardSuitabilityTest {...props} />;
};

export default SuitabilityTest;
